import { useState } from "react";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { RatesTable, type RateDetail } from "../forms/fedex-rates-display";

type RateResponse = {
  transactionId?: string;
  output?: { rateReplyDetails?: RateDetail[] };
  errors?: { code: string; message: string }[];
};

function extractApiMessage(m: unknown): string | undefined {
  if (typeof m === "string") return m;
  if (Array.isArray(m)) {
    const parts = m
      .map((x) =>
        typeof x === "string"
          ? x
          : x && typeof x === "object" && "message" in x
            ? String((x as { message: unknown }).message)
            : "",
      )
      .filter(Boolean);
    return parts.length ? parts.join(", ") : undefined;
  }
  return undefined;
}

export function FedexRatesButton({
  shopId,
  carrierId,
  originPostalCode,
  originCountryCode = "US",
}: {
  shopId: string;
  carrierId?: string;
  originPostalCode?: string;
  originCountryCode?: string;
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RateResponse | null>(null);

  const handleGetRates = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const {
        data: res,
        error: err,
        response,
      } = await fetchClient.POST(
        `/shops/${shopId}/carriers/fedex/rates` as never,
        {
          body: {
            carrierId,
            origin: { postalCode: originPostalCode || "75001", countryCode: originCountryCode },
            destination: { postalCode: "10001", countryCode: "US" },
            weight: { value: 1, units: "KG" },
          },
        } as never,
      );
      if (err || !response.ok) {
        const e = err as
          | { message?: unknown; errors?: { message?: string }[] }
          | undefined;
        const msg = extractApiMessage(e?.message) ?? e?.errors?.[0]?.message;
        throw new Error(msg || `HTTP ${response.status}`);
      }
      setData(res as RateResponse);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={loading}
        onClick={handleGetRates}
      >
        {t("carrier.fedexCsp.getRates")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <img
              src="/fedex.png"
              alt="FedEx"
              className="h-8 w-8 self-start object-contain mb-2 p-2 box-content"
            />
            <DialogTitle>{t("carrier.fedexCsp.ratesTitle")}</DialogTitle>
          </DialogHeader>
          {loading && (
            <p className="text-sm text-gray-500">
              {t("carrier.fedexCsp.ratesLoading")}
            </p>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
          {data?.errors && data.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {data.errors[0].code}: {data.errors[0].message}
            </div>
          )}
          {data && !loading && (
            <div className="space-y-3">
              {data.transactionId && (
                <p className="text-xs text-gray-400">
                  {t("carrier.fedexCsp.ratesTransactionId")}: {data.transactionId}
                </p>
              )}
              <RatesTable rates={data.output?.rateReplyDetails ?? []} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
